import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMoldOrderContext } from '../context/MoldOrderContext';
import { moldOrderService } from '../services/api';
import { MoldOrder, ActionType, OrderDetail } from '../types/mold';
import { PatchMoldOrderRequest } from '../types/moldOrder';

export const useMoldOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [moldOrder, setMoldOrder] = useState<MoldOrder | null>(null);
  const { dispatch } = useMoldOrderContext();

  // Helper to extract status code from error object
  const extractStatusCode = (err: any): number => {
    if (err && typeof err === 'object') {
      if (err.response && typeof err.response.status === 'number') {
        return err.response.status;
      }
      if (typeof err.status === 'number') {
        return err.status;
      }
    }
    return 500;
  };

  const fetchMoldOrderDetail = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      const response = await moldOrderService.getMoldOrderById(parseInt(id), true);
      setMoldOrder(response.data);
    } catch (err: any) {
      console.error('Failed to fetch mold order details:', err);
      setErrorStatus(extractStatusCode(err));
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch mold order details',
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMoldOrderDetailById = useCallback(async (orderId: string, withWorkflowStructure: boolean = false) => {
    try {
      const response = await moldOrderService.getMoldOrderById(parseInt(orderId), withWorkflowStructure);
      return response.data;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch mold order details',
      );
      throw err;
    }
  }, []);

  const updateMoldOrderStatus = useCallback(
    async (action: ActionType, assigneeId?: number | null, orderDetail?: Record<string, any>) => {
      if (!id) return;

      try {
        setLoading(true);
        const payload: any = { action, orderDetail };
        if (assigneeId !== null && assigneeId !== undefined) {
          payload.assignedTo = assigneeId;
        }
        await moldOrderService.submitAction(parseInt(id), payload);
        dispatch({
          type: 'SUBMIT_MOLD_ORDER_ACTION_SUCCESS',
          payload: { id: parseInt(id), action },
        });
        await fetchMoldOrderDetail();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to update mold order status',
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [id, dispatch, fetchMoldOrderDetail],
  );

  const updateMoldOrderDetail = useCallback(
    async (orderDetail?: Partial<OrderDetail>) => {
      if (!id || !orderDetail) return;

      try {
        setLoading(true);
        await moldOrderService.updateOrderDetail(parseInt(id), orderDetail);
        await fetchMoldOrderDetail();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'ไม่สามารถอัปเดตรายละเอียดของใบงานได้',
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [id, dispatch, fetchMoldOrderDetail],
  );

  const updateMoldOrderStateInstance = useCallback(
    async (moldOrder: MoldOrder, data: { notes?: string }) => {
      try {
        // When there are multiple stateInstances with the same status (e.g. reworked),
        // pick the latest one. We assume higher `id` means more recent.
        const matching = moldOrder.stateInstances.filter(item => item.status === moldOrder.status);
        if (matching.length === 0) return;
        const latest = matching.reduce((prev, curr) => (curr.id > prev.id ? curr : prev));
        const instanceId = latest.id;
        if (instanceId) {
          await moldOrderService.updateMoldOrderStateInstance(instanceId, data);
        }
      } catch (err) {
        setError('Failed to update mold order state instance');
      }
    },
    []
  );

  const updateMoldOrder = useCallback(
    async (data: PatchMoldOrderRequest) => {
      if (!id) return;

      try {
        setLoading(true);
        await moldOrderService.updateMoldOrder(parseInt(id), data);
        await fetchMoldOrderDetail();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to update mold order',
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [id, fetchMoldOrderDetail],
  );

  const updateMoldOrderById = useCallback(
    async (id: number, data: PatchMoldOrderRequest) => {
      if (!id) return;

      try {
        setLoading(true);
        await moldOrderService.updateMoldOrder(id, data);
        await fetchMoldOrderDetail();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to update mold order',
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchMoldOrderDetail],
  );

  const downloadPdfReport = useCallback(async (id: number): Promise<Blob | null> => {
    if (!id) return null;

    try {
      const response = await moldOrderService.downloadPdfReport(id);
      return response.data;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to download pdf report',
      );
      throw err;
    }
  }, []);

  const submitApproval = useCallback(
    async (data: {
      stateInstanceId: number;
      approverInstanceId: number;
      action: 'approve' | 'reject' | 'rework';
      comments?: string;
    }) => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        await moldOrderService.submitApproval(parseInt(id), data);
        await fetchMoldOrderDetail();
      } catch (err: any) {
        console.error('Failed to submit approval:', err);
        const errorMessage = err?.response?.data?.message || 
          err?.message || 
          'ไม่สามารถส่งการอนุมัติได้';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [id, fetchMoldOrderDetail],
  );

  useEffect(() => {
    fetchMoldOrderDetail();
  }, [fetchMoldOrderDetail]);

  return {
    moldOrder,
    loading,
    error,
    errorStatus,
    updateMoldOrderStatus,
    updateMoldOrderStateInstance,
    updateMoldOrder,
    updateMoldOrderById,
    updateMoldOrderDetail,
    refetch: fetchMoldOrderDetail,
    fetchMoldOrderDetail,
    fetchMoldOrderDetailById,
    downloadPdfReport,
    submitApproval,
  };
};
